import React, { useState, useMemo } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    TextField,
    FormControl,
    Select,
    MenuItem,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    ListItemIcon,
    Chip,
    InputAdornment,
    useTheme,
    Divider,
    Badge,
    Stack,
    Grid,
    LinearProgress,
    Tooltip
} from '@mui/material';
import {
    CheckCircle,
    RadioButtonUnchecked,
    Search,
    Send,
    FilterList,
    Done,
    ErrorOutline,
    WarningAmber,
    Pending
} from '@mui/icons-material';
import { tokens } from '../../../../theme';

const QuestionNavigator = ({
    questions,
    currentIndex,
    reviews,
    onQuestionSelect,
    filterStatus,
    onFilterChange,
    onSubmitAll,
    canSubmit
}) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [searchQuery, setSearchQuery] = useState('');

    // Filter and search questions
    const filteredQuestions = useMemo(() => {
        let filtered = questions;

        // Apply status filter
        if (filterStatus === 'reviewed') {
            filtered = filtered.filter(q => reviews[q.item_id]);
        } else if (filterStatus === 'pending') {
            filtered = filtered.filter(q => !reviews[q.item_id]);
        }

        // Apply search filter
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter((q, index) => {
                const questionText = q.question_data?.question_text || 
                                   q.question_data?.question || '';
                return (
                    questionText.toLowerCase().includes(query) ||
                    `question ${index + 1}`.includes(query)
                );
            });
        }

        return filtered;
    }, [questions, filterStatus, reviews, searchQuery]);

    const getQuestionStatus = (question) => {
        const review = reviews[question.item_id];
        if (!review) return null;
        
        if (review.status === 'looks_good') return 'success';
        if (review.status === 'has_error') return 'error';
        if (review.status === 'needs_revision') return 'warning';
        return null;
    };

    const getStatusIcon = (question) => {
        const status = getQuestionStatus(question);
        
        if (status === 'success') {
            return <CheckCircle color='success' />;
        }
        if (status === 'error') {
            return <ErrorOutline color='error' />;
        }
        if (status === 'warning') {
            return <WarningAmber color='warning' />;
        }
        
        return <RadioButtonUnchecked sx={{ color: colors.grey[500] }} />;
    };

    const getQuestionLabel = (question) => {
        const actualIndex = questions.indexOf(question);
        const questionText = question.question_data?.question_text || 
                           question.question_data?.question || 
                           'No question text';
        
        // Strip HTML and truncate
        const plainText = questionText.replace(/<[^>]*>/g, '').substring(0, 80);
        
        return {
            primary: `Question ${actualIndex + 1}`,
            secondary: plainText + (plainText.length >= 80 ? '...' : '')
        };
    };

    // Statistics
    const stats = useMemo(() => {
        const reviewed = Object.keys(reviews).length;
        const lookGood = Object.values(reviews).filter(r => r.status === 'looks_good').length;
        const hasError = Object.values(reviews).filter(r => r.status === 'has_error').length;
        const needsRevision = Object.values(reviews).filter(r => r.status === 'needs_revision').length;
        const percentage = questions.length > 0 ? Math.round((reviewed / questions.length) * 100) : 0;
        
        return { reviewed, lookGood, hasError, needsRevision, percentage };
    }, [reviews, questions]);

    return (
        <Box height="80vh" display="flex" flexDirection="column">
            {/* Statistics Dashboard */}
            <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Review Progress
                </Typography>
                <Box mb={2}>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="body2" color="text.secondary">
                            {stats.reviewed} of {questions.length} reviewed
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                            {stats.percentage}%
                        </Typography>
                    </Box>
                    <LinearProgress 
                        variant="determinate" 
                        value={stats.percentage}
                        sx={{
                            height: 8,
                            borderRadius: 4,
                            '& .MuiLinearProgress-bar': {
                                borderRadius: 4
                            }
                        }}
                    />
                </Box>
                <Grid container spacing={1}>
                    <Grid item xs={3}>
                        <Box 
                            sx={{ 
                                p: 1, 
                                textAlign: 'center',
                                border: `1px solid ${colors.grey[700]}`,
                                borderRadius: 1,
                                height: '80px',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center'
                            }}
                        >
                            <Done sx={{ color: colors.greenAccent[400], fontSize: 20 }} />
                            <Typography variant="h6">
                                {stats.lookGood}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Good
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={3}>
                        <Box 
                            sx={{ 
                                p: 1, 
                                textAlign: 'center',
                                border: `1px solid ${colors.grey[700]}`,
                                borderRadius: 1,
                                height: '80px',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center'
                            }}
                        >
                            <ErrorOutline sx={{ color: colors.redAccent[400], fontSize: 20 }} />
                            <Typography variant="h6">
                                {stats.hasError}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Errors
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={3}>
                        <Box 
                            sx={{ 
                                p: 1, 
                                textAlign: 'center',
                                border: `1px solid ${colors.grey[700]}`,
                                borderRadius: 1,
                                height: '80px',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center'
                            }}
                        >
                            <WarningAmber sx={{ color: colors.yellowAccent[400], fontSize: 20 }} />
                            <Typography variant="h6">
                                {stats.needsRevision}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Revision
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={3}>
                        <Box 
                            sx={{ 
                                p: 1, 
                                textAlign: 'center',
                                border: `1px solid ${colors.grey[700]}`,
                                borderRadius: 1,
                                height: '80px',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center'
                            }}
                        >
                            <Pending sx={{ color: colors.grey[400], fontSize: 20 }} />
                            <Typography variant="h6">
                                {questions.length - stats.reviewed}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Pending
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {/* Search and Filter */}
            <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
                <Stack spacing={2}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search questions..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search />
                                </InputAdornment>
                            )
                        }}
                    />
                    <FormControl size="small" fullWidth>
                        <Select
                            value={filterStatus}
                            onChange={(e) => onFilterChange(e.target.value)}
                            displayEmpty
                            startAdornment={
                                <InputAdornment position="start">
                                    <FilterList />
                                </InputAdornment>
                            }
                        >
                            <MenuItem value="all">All Questions</MenuItem>
                            <MenuItem value="reviewed">Reviewed Only</MenuItem>
                            <MenuItem value="pending">Pending Only</MenuItem>
                        </Select>
                    </FormControl>
                </Stack>
            </Paper>

            {/* Question List */}
            <Paper elevation={2} sx={{ 
                flex: 1, 
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                mb: 2,
                minHeight: 0
            }}>
                <List sx={{ 
                    p: 0,
                    overflow: 'auto',
                    flex: 1
                }}>
                    {filteredQuestions.map((question) => {
                        const actualIndex = questions.indexOf(question);
                        const isSelected = actualIndex === currentIndex;
                        const status = getQuestionStatus(question);
                        const label = getQuestionLabel(question);
                        
                        return (
                            <React.Fragment key={question.item_id}>
                                <ListItem
                                    disablePadding
                                    sx={{
                                        bgcolor: isSelected ? colors.primary[900] : 'transparent',
                                        borderLeft: isSelected ? `4px solid ${colors.primary[500]}` : '4px solid transparent',
                                        '&:hover': {
                                            bgcolor: colors.grey[900]
                                        }
                                    }}
                                >
                                    <ListItemButton
                                        onClick={() => onQuestionSelect(actualIndex)}
                                        sx={{ py: 2 }}
                                    >
                                        <ListItemIcon>
                                            <Tooltip title={
                                                status === 'success' ? 'Looks Good' :
                                                status === 'error' ? 'Has Error' :
                                                status === 'warning' ? 'Needs Revision' :
                                                'Not Reviewed'
                                            }>
                                                <Badge
                                                    badgeContent={question.is_required ? '!' : null}
                                                    color="warning"
                                                >
                                                    {getStatusIcon(question)}
                                                </Badge>
                                            </Tooltip>
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Typography 
                                                        fontWeight={isSelected ? 'bold' : 'medium'}
                                                        color={isSelected ? 'primary' : 'text.primary'}
                                                    >
                                                        {label.primary}
                                                    </Typography>
                                                    {question.is_required && (
                                                        <Chip 
                                                            label="Required" 
                                                            size="small" 
                                                            color="warning"
                                                            sx={{ height: 20 }}
                                                        />
                                                    )}
                                                </Box>
                                            }
                                            secondary={
                                                <Typography 
                                                    variant="caption" 
                                                    color="text.secondary"
                                                    sx={{
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: 'hidden'
                                                    }}
                                                >
                                                    {label.secondary}
                                                </Typography>
                                            }
                                        />
                                    </ListItemButton>
                                </ListItem>
                                <Divider />
                            </React.Fragment>
                        );
                    })}
                </List>
                
                {filteredQuestions.length === 0 && (
                    <Box p={4} textAlign="center">
                        <Typography color="text.secondary">
                            No questions found matching your criteria
                        </Typography>
                    </Box>
                )}
            </Paper>

            {/* Submit Button */}
            <Button
                variant="contained"
                color="success"
                fullWidth
                size="large"
                startIcon={<Send />}
                onClick={onSubmitAll}
                disabled={!canSubmit}
                sx={{
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    boxShadow: 3,
                    '&:hover': {
                        boxShadow: 6
                    }
                }}
            >
                Submit All Reviews ({stats.reviewed}/{questions.length})
            </Button>
        </Box>
    );
};

export default QuestionNavigator;